/**
 * @constant API_CONFIG
 * @description Define la configuración de la API.
 */
export const API_CONFIG = {
  /**
   * @property {string} BASE_URL - La URL base de la API.
   */
  BASE_URL: 'https://localhost:7260/api',
  /**
   * @property {string} MATERIALIDAD - La URL base de la API Materialidad.
   */
  MATERIALIDAD: 'https://localhost:44389/api',

  /**
   * @property {object} ALTERNATIVE_URLS - Las URLs alternativas de la API.
  */
  ALTERNATIVE_URLS: {
    HTTPS: 'https://localhost:7260/api',
    HTTP: 'http://localhost:7260/api',
    LOCAL: 'http://127.0.0.1:7260/api',
    NETWORK: 'http://[TU_IP]:7260/api',
    DEV: 'https://dev.ssuite.grupoteckio.com/api/',
    QA: 'https://qa.smanfora.gruporfacil.com/api/',
    TEST: 'https://test.smanfora.gruporfacil.com/api/',
    DEMO: 'https://demo.smaterialidad.gruporfacil.com/api/',
    PRODUCTION: 'https://sso.rfacil.com/api/',
  },
  /**
   * @property {object} ALTERNATIVE_URLS_MATERIALIDAD - Las URLs alternativas de la API Materialidad.
   */
  ALTERNATIVE_URLS_MATERIALIDAD: {
    HTTPS: 'https://localhost:44389/api',
    HTTP: 'http://localhost:44389/api',
    LOCAL: 'http://127.0.0.1:44389/api',
    NETWORK: 'http://[TU_IP]:44389/api',
    DEV: 'https://dev.bmsuite.grupoteckio.com/api/',
    QA: 'https://qa.bmanfora.gruporfacil.com/api/',
    TEST: 'https://test.bmanfora.gruporfacil.com/api/',
    DEMO: 'https://demo.bmaterialidad.gruporfacil.com/api/',
    PRODUCTION: 'https://anforamb.rfacil.com/api/',
  },



  /**
   * @property {number} TIMEOUT - El tiempo de espera de la API.
   */
  TIMEOUT: 15000,

  /**
   * @property {object} DEFAULT_HEADERS - Los headers por defecto de la API.
   */
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
} as const;

/**
 * @function resolveEnvironmentApiUrl
 * @description Resuelve la URL base de la API principal (SSO/host) a partir de las variables
 * de entorno de Vite. En producción prioriza `VITE_API_URL_PRODUCTION`; en desarrollo usa
 * `VITE_API_URL`. Retorna `undefined` si ninguna variable está definida.
 * @returns {string | undefined} La URL base resuelta desde las variables de entorno, o `undefined`
 * si no hay ninguna disponible.
 */
const resolveEnvironmentApiUrl = (): string | undefined => {
  const mode = import.meta.env.MODE;
  const isLocalDev = mode === 'development' || !mode;

  const viteApiUrlProduction = import.meta.env.VITE_API_URL_PRODUCTION;
  const viteApiUrl = import.meta.env.VITE_API_URL;
  const viteApiUrlLocal = import.meta.env.VITE_API_URL_LOCAL;

  // En desarrollo local, .env.local define VITE_API_URL apuntando a localhost
  if (isLocalDev) {
    return viteApiUrl || viteApiUrlProduction || viteApiUrlLocal || undefined;
  }

  // Para builds de despliegue (dev, qa, test, production):
  // .env.local siempre se carga por Vite pero VITE_API_URL no debe sobrescribir
  // la URL del ambiente destino definida en VITE_API_URL_PRODUCTION
  return viteApiUrlProduction || viteApiUrlLocal || undefined;
};

/**
 * @function getApiBaseUrl
 * @description Obtiene la URL base de la API principal (SSO/host) según el entorno de ejecución.
 * Delega primero a `resolveEnvironmentApiUrl`; si no hay variables de entorno configuradas,
 * usa `API_CONFIG.BASE_URL` en desarrollo o `API_CONFIG.ALTERNATIVE_URLS.PRODUCTION` en producción.
 * @returns {string} La URL base de la API principal lista para usar en instancias de Axios.
 */
export const getApiBaseUrl = (): string => {
  const environmentUrl = resolveEnvironmentApiUrl();
  if (environmentUrl) {
    return environmentUrl;
  }

  const mode = import.meta.env.MODE;

  if (mode === 'dev') return API_CONFIG.ALTERNATIVE_URLS.DEV;
  if (mode === 'qa') return API_CONFIG.ALTERNATIVE_URLS.QA;
  if (mode === 'test') return API_CONFIG.ALTERNATIVE_URLS.TEST;
  if (mode === 'demo') return API_CONFIG.ALTERNATIVE_URLS.DEMO;
  if (mode === 'production') return API_CONFIG.ALTERNATIVE_URLS.PRODUCTION;

  return API_CONFIG.BASE_URL;
};
/**
 * @function resolveEnvironmentMaterialidadApiUrl
 * @description Resuelve la URL base de la API Materialidad a partir de las variables de entorno
 * de Vite. En producción prioriza `VITE_MATERIALIDAD_API_URL_PRODUCTION`; en desarrollo usa
 * `VITE_MATERIALIDAD_API_URL`. Retorna `undefined` si ninguna variable está definida.
 * @returns {string | undefined} La URL base resuelta desde las variables de entorno, o `undefined`
 * si no hay ninguna disponible.
 */
const resolveEnvironmentMaterialidadApiUrl = (): string | undefined => {
  const mode = import.meta.env.MODE;
  const isLocalDev = mode === 'development' || !mode;

  const viteMaterialidadApiUrlProduction = import.meta.env.VITE_MATERIALIDAD_API_URL_PRODUCTION;
  const viteMaterialidadApiUrl = import.meta.env.VITE_MATERIALIDAD_API_URL;
  const viteMaterialidadApiUrlLocal = import.meta.env.VITE_MATERIALIDAD_API_URL_LOCAL;

  if (isLocalDev) {
    return viteMaterialidadApiUrl || viteMaterialidadApiUrlProduction || viteMaterialidadApiUrlLocal || undefined;
  }

  return viteMaterialidadApiUrlProduction || viteMaterialidadApiUrlLocal || undefined;
};
/**
 * @function getMaterialidadBaseUrl
 * @description Obtiene la URL base de la API Materialidad según el entorno.
 * @returns {string} - La URL base de la API Materialidad.
 */
export const getMaterialidadBaseUrl = (): string => {
  const environmentUrl = resolveEnvironmentMaterialidadApiUrl();
  if (environmentUrl) {
    return environmentUrl;
  }

  const mode = import.meta.env.MODE;

  if (mode === 'dev') return API_CONFIG.ALTERNATIVE_URLS_MATERIALIDAD.DEV;
  if (mode === 'qa') return API_CONFIG.ALTERNATIVE_URLS_MATERIALIDAD.QA;
  if (mode === 'test') return API_CONFIG.ALTERNATIVE_URLS_MATERIALIDAD.TEST;
  if (mode === 'demo') return API_CONFIG.ALTERNATIVE_URLS_MATERIALIDAD.DEMO;
  if (mode === 'production') return API_CONFIG.ALTERNATIVE_URLS_MATERIALIDAD.PRODUCTION;

  return API_CONFIG.MATERIALIDAD;
};




